import React, { useState, useEffect, useCallback } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { Calendar, Loader2, ChevronRight } from 'lucide-react'

interface Template {
  id: number
  name: string
  start_date: string
  end_date: string
  modifier_type: string
  modifier_value: number
  priority: number
}

interface GroupedTemplates {
  [group: string]: Template[]
}

export interface SelectedTemplate {
  name: string
  startDate: string
  endDate: string
  modifierType: string
  modifierValue: number
  priority: number
}

interface SeasonTemplateSelectorProps {
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (template: SelectedTemplate) => void
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatModifier(type: string, value: number): string {
  if (type === 'percentage') {
    return `${value > 0 ? '+' : ''}${value}%`
  }
  return `${value > 0 ? '+' : ''}${value} GBP`
}

export default function SeasonTemplateSelector({
  propertyBasePath,
  open,
  onOpenChange,
  onSelect,
}: SeasonTemplateSelectorProps) {
  const [templates, setTemplates] = useState<GroupedTemplates>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // apiFetch, not a bare fetch: it sources the CSRF token from the cookie at request time
      // (the meta tag is frozen at page load and goes stale on any session rotation) and raises a
      // user-facing SessionExpiredError. Same rule as every other JSON call in the app.
      const response = await apiFetch(`${propertyBasePath}/seasons/templates`, {
        method: 'GET',
      })
      if (!response.ok) throw new Error('Failed to load templates')
      const data = await response.json()
      setTemplates(data.templates || {})
    } catch (err) {
      setError(apiErrorMessage(err, err instanceof Error ? err.message : 'An error occurred'))
    } finally {
      setLoading(false)
    }
  }, [propertyBasePath])

  useEffect(() => {
    if (open) {
      fetchTemplates()
    }
  }, [open, fetchTemplates])

  function handleSelect(template: Template) {
    onSelect({
      name: template.name,
      startDate: template.start_date,
      endDate: template.end_date,
      modifierType: template.modifier_type,
      modifierValue: template.modifier_value,
      priority: template.priority,
    })
    onOpenChange(false)
  }

  const groups = Object.keys(templates)
  const hasTemplates = groups.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Season Templates</DialogTitle>
          <DialogDescription>
            Choose a pre-configured season template. You can adjust dates before saving.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={fetchTemplates}
              >
                Try Again
              </Button>
            </div>
          )}

          {!loading && !error && !hasTemplates && (
            <div className="py-12 text-center">
              <Calendar className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No templates available</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Templates are managed by your system administrator.
              </p>
            </div>
          )}

          {!loading && !error && hasTemplates && (
            <div className="space-y-5">
              {groups.map((group) => (
                <div key={group}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group}
                  </h3>
                  <div className="space-y-2">
                    {templates[group].map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        className="group w-full rounded-xl border border-border/60 bg-white p-3.5 text-left shadow-sm transition-all hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onClick={() => handleSelect(template)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-foreground">
                                {template.name}
                              </span>
                              <Badge
                                variant="secondary"
                                className={cn(
                                  'text-[10px] font-semibold',
                                  template.modifier_value > 0 &&
                                    'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
                                  template.modifier_value < 0 &&
                                    'bg-rose-100 text-rose-700 hover:bg-rose-100',
                                  template.modifier_value === 0 &&
                                    'bg-slate-100 text-slate-600 hover:bg-slate-100'
                                )}
                              >
                                {formatModifier(template.modifier_type, template.modifier_value)}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {formatDate(template.start_date)} &ndash;{' '}
                              {formatDate(template.end_date)}
                              <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wider">
                                Priority {template.priority}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
